import { useGo, useTranslate } from "@refinedev/core";
import { useTable } from "@refinedev/antd";
import { Button, Card, Flex } from "antd";
import { useDeferredValue, useEffect, useMemo } from "react";
import { MediaProjectListItem } from "../../types";
import { useBoundStore } from "store/";
import { useOrganization } from "hooks/useOrganization/";
import { useParams } from "react-router-dom";
import { EmptyList } from "../emptyList";
import SearchInput from "../../projects/components/SearchInput";
import { CreatePrimaryButton } from "components/CreatePrimaryButton/";
import {
  createHandleTableSearch,
  useResetPaginationOnPageSizeChange,
} from "helpers/table/tableUtils";
import { createProjectColumns } from "helpers/table/projectColumns";
import { useAntTheme } from "hooks/useAntTheme/";
import { PAGE_SIZE } from "../../projects";
import { getProjectDetailRoute } from "../../projects/helpers/helper";
import { ProjectsTable } from "./projects";
import { radius } from "@libertify/ui";

export const ExperiencesTable = () => {
  const t = useTranslate();
  const params = useParams();
  const workspaceId = params.id;
  const go = useGo();
  const projectsState = useBoundStore((state) => state.projectsState);
  const { pagination, searchText } = projectsState;
  const deferredSearchText = useDeferredValue(searchText);
  const { currentOrganization, getOrganization } = useOrganization({});
  const setProjectsState = useBoundStore((state) => state.setProjectsState);
  const { theme } = useAntTheme();

  const handleProjectClick = (record: MediaProjectListItem) => {
    go({ to: getProjectDetailRoute(record.id, record.type) });
  };

  const handleCreateProject = () => {
    go({
      to: {
        resource: "media_projects",
        action: "create",
      },
      query: workspaceId ? { workspace_id: workspaceId } : undefined,
    });
  };

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (workspaceId) {
      params.append("workspace_id", workspaceId);
    }
    const queryStr = params.toString();
    return queryStr ? `?${queryStr}` : "";
  }, [workspaceId]);

  const resource = `media/${currentOrganization?.id}/projects${queryString}`;
  const {
    tableProps,
    tableQuery: { isLoading, data: projectsData },
    setCurrent,
    setFilters,
  } = useTable<MediaProjectListItem>({
    resource,
    queryOptions: {
      enabled: Boolean(currentOrganization),
    },
    pagination: {
      pageSize: PAGE_SIZE,
    },
    sorters: {
      initial: [
        {
          field: "created",
          order: "desc",
        },
      ],
    },
  });

  useEffect(() => {
    setCurrent(1);
    if (deferredSearchText) {
      setFilters([
        { field: "search_text", operator: "eq", value: deferredSearchText },
      ]);
    } else {
      setFilters([], "replace");
    }
  }, [deferredSearchText]);

  const columns = createProjectColumns({
    t,
    getOrganization,
    handleProjectClick,
    organizationFilterValue: projectsState.filters.organization_id,
    invalidationResource: resource,
  });

  const handleTableSearch = createHandleTableSearch(setProjectsState);

  useResetPaginationOnPageSizeChange(
    setProjectsState,
    projectsState,
    pagination
  );

  const hasNoInitialData = projectsData?.data?.length === 0;
  return (
    <>
      <Flex gap={10} justify="space-between" style={{ margin: "20px 0" }}>
        <div style={{ width: "30%" }}>
          <SearchInput
            onSearch={handleTableSearch}
            value={searchText}
            size="large"
            placeholder={t("projects.components.SearchInput.searchExperiences")}
          />
        </div>
        <CreatePrimaryButton onClick={handleCreateProject}>
          {t("media.workspaces.experiences.newExperience")}
        </CreatePrimaryButton>
      </Flex>
      {hasNoInitialData ? (
        <EmptyList
          title={t("media.workspaces.experiences.noExperiencesYet")}
          description={t("media.workspaces.experiences.getStartedExperience")}
        >
          <Button type="primary" onClick={handleCreateProject}>
            {t("media.workspaces.experiences.createExperience")}
          </Button>
        </EmptyList>
      ) : (
        <Card
          style={{
            borderColor: theme.colorLightGrey,
            backgroundColor: theme.colorBgLayout,
            borderRadius: radius.box,
            overflow: "hidden",
          }}
          styles={{
            body: {
              padding: 0,
            },
          }}
        >
          <ProjectsTable
            tableProps={tableProps}
            isLoading={isLoading}
            columns={columns}
            data={projectsData?.data ?? []}
          />
        </Card>
      )}
    </>
  );
};
